import React, {useEffect, Suspense, useCallback, useRef, useState} from 'react'
import * as PropTypes from 'prop-types'
import {CustomerUploadManager} from '@modifi/plugin-documents'
import {InferPropTypes} from '@modifi/typescript-utils'
import {
  Row,
  Col,
  CheckboxPure as CheckboxPure_,
  Strong,
  Paragraph,
  H4,
  Tooltip,
  HelpIcon,
  DangerAlert,
  SelectPure as SelectPure_,
  Pagination,
} from '@modifi/ui'
import {times} from 'ramda'
import {useParams} from '@modifi/router'
import {validate as validateUuid} from 'uuid'
import {useTranslation} from '@modifi/plugin-i18n'
import {TrackingNotificationStatus} from '@modifi/enums'
import {darkGray, seaNavy} from '@modifi/colors'
import qs from 'qs'
import {SubmitShipment, ShipmentEntry} from './components'
import {getPageSizeOptions, SHIPMENT_DETAILS_SORTING_OPTIONS} from './constants'
import {withSuspense} from '../../hoc'
import ShipIcon from '../../ui/ship-icon'
import {NotificationCheckBoxContainer, ShipmentsEmptyState, TooltipIconWrapper} from './styles'

const SelectPure = SelectPure_ as React.ComponentType<any>

const CheckboxPure = CheckboxPure_ as React.ComponentType<any>

const customStyles = {
  container: {
    fontSize: '14px',
  },
  control: {
    width: 150,
    borderWidth: '0px',
    background: 'transparent',
    minHeight: '30px',
    paddingLeft: '9px',
    paddingRight: '9px',
  },
  valueContainer: {
    height: '28px',
    padding: 0,
  },
  option: {
    padding: '8px 10px',
  },
  dropdownIndicator: {
    height: 16,
    width: 16,
    marginTop: -2,
  },
}

const InfoIcon = React.forwardRef((props, ref) => (
  // @ts-expect-error
  <TooltipIconWrapper ref={ref}>
    <HelpIcon color={darkGray} width={24} height={24} {...props} />
  </TooltipIconWrapper>
))

const propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  search: PropTypes.object.isRequired,
  fetchParams: PropTypes.shape({
    pageIndex: PropTypes.number,
    pageSize: PropTypes.number,
    orderBy: PropTypes.arrayOf(PropTypes.string),
    orderDirection: PropTypes.arrayOf(PropTypes.oneOf(['ASC', 'DESC'])),
  }).isRequired,
  shipmentsWhereLoaded: PropTypes.bool.isRequired,
  shipments: PropTypes.arrayOf(
    PropTypes.shape({
      externalId: PropTypes.string,
      shippingStartDate: PropTypes.string,
      estimatedShippingStartDate: PropTypes.string,
      shippingEndDate: PropTypes.string,
      estimatedShippingEndDate: PropTypes.string,
      shipmentStatus: PropTypes.string,
      containers: PropTypes.arrayOf(
        PropTypes.shape({
          containerId: PropTypes.string,
        }).isRequired
      ),
      destination: PropTypes.shape({
        name: PropTypes.string,
        countryCode: PropTypes.string,
      }),
      origin: PropTypes.shape({
        name: PropTypes.string,
        countryCode: PropTypes.string,
      }),
      documentRef: PropTypes.shape({
        documentType: PropTypes.string,
        fileId: PropTypes.string,
      }),
      statusInfo: PropTypes.string,
    }).isRequired
  ).isRequired,
  errorObject: PropTypes.shape({
    errorMessage: PropTypes.string,
    errorCode: PropTypes.number,
  }).isRequired,
  redirect: PropTypes.func.isRequired,
  fetchShipments: PropTypes.func.isRequired,
  redirectWithDefaultQuery: PropTypes.func.isRequired,
  redirectToShipmentsOverview: PropTypes.func.isRequired,
  showDetails: PropTypes.func.isRequired,
  shipmentsMeta: PropTypes.shape({
    maxPageIndex: PropTypes.number,
    totalCount: PropTypes.number,
  }),
  userSettings: PropTypes.oneOf(Object.keys(TrackingNotificationStatus)),
  fetchUserSettings: PropTypes.func.isRequired,
  updateUserSettings: PropTypes.func.isRequired,
  showErrorDialog: PropTypes.func.isRequired,
}
const defaultProps = {
  shipmentsMeta: undefined as null | undefined,
  userSettings: undefined,
}

export type ShipmentsProps = InferPropTypes<typeof propTypes, typeof defaultProps>

const Shipments: React.VFC<ShipmentsProps> = ({
  showErrorDialog,
  errorObject,
  fetchShipments,
  shipments,
  fetchParams,
  redirectWithDefaultQuery,
  shipmentsMeta,
  shipmentsWhereLoaded,
  showDetails,
  redirectToShipmentsOverview,
  userSettings,
  fetchUserSettings,
  updateUserSettings,
  redirect,
  search,
}) => {
  const {t} = useTranslation(['argus', 'ui'])
  const submitStrategyRef = useRef<'FORM' | 'DOCUMENT'>()
  const {pageIndex, pageSize, orderBy, orderDirection} = fetchParams
  const {shipmentId} = useParams<{shipmentId?: string}>()
  const {errorMessage: submitErrorMessage, errorCode: submitErrorCode} = errorObject
  const [selectedSortValue, selectedSortingValue] = useState<number>(0)

  useEffect(() => {
    fetchUserSettings()
  }, [fetchUserSettings])

  useEffect(() => {
    if (submitErrorCode === 409 && submitErrorMessage !== undefined) {
      showErrorDialog({error: submitErrorMessage})
    }
  }, [submitErrorCode, submitErrorMessage, showErrorDialog])

  useEffect(() => {
    if (shipmentId) {
      if (validateUuid(shipmentId)) {
        showDetails(shipmentId)
      }
      redirectToShipmentsOverview()
    }
  }, [redirectToShipmentsOverview, shipmentId, showDetails])

  useEffect(() => {
    if (typeof pageSize === 'number' && typeof pageIndex === 'number') {
      fetchShipments({
        pageSize,
        pageIndex,
        orderBy: orderBy || ['status'],
        orderDirection: orderDirection || ['DESC'],
      })
    } else {
      redirectWithDefaultQuery()
    }
  }, [fetchShipments, redirectWithDefaultQuery, pageSize, pageIndex, orderBy, orderDirection])

  const handleUpdateUserSettings = useCallback(() => {
    updateUserSettings({
      emailNotifications:
        userSettings === TrackingNotificationStatus.OWN_SHIPMENTS
          ? TrackingNotificationStatus.DISABLED
          : TrackingNotificationStatus.OWN_SHIPMENTS,
    })
  }, [updateUserSettings, userSettings])

  const onSortChange = (event: any) => {
    const selectedOption = SHIPMENT_DETAILS_SORTING_OPTIONS.find((x: any) => x.id === event.value)
    selectedSortingValue(selectedOption !== undefined ? selectedOption.id : 0)
    redirect({
      search: qs.stringify({
        ...search,
        itemsPerPage: pageSize,
        page: 1,
        orderBy: [selectedOption !== undefined ? selectedOption.field : ''],
        orderDirection: [selectedOption?.orderBy],
      }),
    })
  }

  const updateSubmitStrategy = useCallback((submitStrategy: 'FORM' | 'DOCUMENT') => {
    submitStrategyRef.current = submitStrategy
  }, [])

  return (
    <CustomerUploadManager>
      {submitErrorCode !== 409 && submitErrorMessage && submitStrategyRef.current === 'FORM' && (
        <DangerAlert>{submitErrorMessage}</DangerAlert>
      )}
      <H4 mb={24}>{t('shipment-tracker.title')}</H4>
      <div data-track-category="shipments-page">
        <Suspense fallback={null}>
          <SubmitShipment setSubmitStrategy={updateSubmitStrategy} />
        </Suspense>
        {shipmentsWhereLoaded && !!shipments.length && (
          <Row justifyContent="space-between" alignItems="center" my={16}>
            <Col col={6}>
              <SelectPure
                options={[
                  {label: t('shipment-sorting-start-date-asc'), value: 1},
                  {label: t('shipment-sorting-start-date-desc'), value: 2},
                  {label: t('shipment-sorting-end-date-asc'), value: 3},
                  {label: t('shipment-sorting-end-date-desc'), value: 4},
                  {label: t('shipment-sorting-last-event-date-asc'), value: 5},
                  {label: t('shipment-sorting-last-event-date-desc'), value: 6},
                ]}
                onChange={onSortChange}
                value={selectedSortValue}
                customStyles={customStyles}
                isSearchable={false}
              />
            </Col>
            <NotificationCheckBoxContainer col={6}>
              <Tooltip refComponent={InfoIcon}>
                {t('shipment-notification-enabler.tooltip')}
              </Tooltip>
              <CheckboxPure
                data-name="shipment-notification-enabler"
                name="shipment-notification-enabler"
                label={t('shipment-notification-enabler.label')}
                disabled={false}
                checked={userSettings === TrackingNotificationStatus.OWN_SHIPMENTS}
                checkboxPosition="center"
                onChange={handleUpdateUserSettings}
              />
            </NotificationCheckBoxContainer>
          </Row>
        )}

        <Suspense fallback={null}>
          {shipmentsWhereLoaded && !shipments.length && (
            <Row justifyContent="center">
              <ShipmentsEmptyState col={12}>
                <ShipIcon color={seaNavy} width={140} height={40} />
                <Strong>{t('empty-shipments.message')}</Strong>
                <Paragraph>{t('empty-shipments.description')}</Paragraph>
              </ShipmentsEmptyState>
            </Row>
          )}
        </Suspense>

        <Suspense fallback="loading">
          <Row>
            <Col data-ident="shipments-tracking-list">
              {!shipmentsWhereLoaded
                ? times(index => <ShipmentEntry key={index} isLoading />, pageSize ?? 6)
                : shipments.map(({externalId, ...rest}, index) => (
                    <Suspense fallback={<ShipmentEntry />} key={externalId}>
                      <ShipmentEntry externalId={externalId} listIndex={index} {...rest} />
                    </Suspense>
                  ))}
            </Col>
          </Row>
        </Suspense>
        <Suspense fallback={null}>
          {shipmentsWhereLoaded && !!shipments.length && (
            <Pagination
              totalCount={shipmentsMeta?.totalCount ?? 0}
              maxPageIndex={shipmentsMeta?.maxPageIndex ?? 0}
              pageSizeOptions={getPageSizeOptions(t)}
            />
          )}
        </Suspense>
      </div>
    </CustomerUploadManager>
  )
}

Shipments.propTypes = propTypes
Shipments.defaultProps = defaultProps

export default withSuspense(false)(Shipments)
